import React, { useState, useEffect } from "react";
import { Formik, Form, useField } from "formik";
import { parsePhoneNumber } from "libphonenumber-js";
import { eyeTestCodeToLabel } from "../../services/data";
import moment from "moment";
import FormikErrorFocus from "formik-error-focus";
import {
  TextFieldWithLabel,
  Checkbox,
  Error,
  Radio,
  GoogleAddress,
  Select,
  DateField,
  Label,
} from "../input/index.ts";
import RequiredDot from "../input/RequiredDot.tsx";
import Button from "../system/buttons/Button.tsx";
import { invalidEmailAddresses } from "./invalidEmailAddresses";

const emailAddressRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/gim;
const postcodeRegex = /^\d{4}$/gim;
const ndssRegex = /^\d{5,9}$/gim;

const FormHeader = ({ children }) => (
  <div className="mb-2 font-semibold text-xl">{children}</div>
);

const CheckboxWithField = ({
  name,
  label,
  children,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
}) => {
  const [{ value, onChange, onBlur }] = useField(name);
  const [fieldShowing, setFieldShowing] = useState(value);

  useEffect(() => {
    setFieldShowing(value);
  }, [value]);

  return (
    <div className="my-1" data-testid={`${name}-checkbox`}>
      <Checkbox
        id={name}
        name={name}
        label={label}
        value={value}
        defaultChecked={value === "true" || value === true}
        onChange={onChange}
        onBlur={onBlur}
        ariaDescribedBy={ariaDescribedBy}
        aria-required={ariaRequired}
        aria-invalid={ariaInvalid}
      />
      {fieldShowing && <div className="md:max-w-sm">{children}</div>}
    </div>
  );
};

const DefaultSignUpExplainer = () => (
  <>
    <h2 className="mb-3">Welcome to KeepSight</h2>
    <div className="flex items-center">
      <div className="md:max-w-prose">
        <p>
          We understand there's a lot to manage when you have diabetes.
          KeepSight, from Diabetes Australia, will remind you when you are due
          for an eye check.
        </p>
        <p>
          Fill out the form below, and we'll remind you when it's time to get
          your eyes tested.
        </p>
        <p>
          We will even help you find an optometrist if you don't already have
          one.
        </p>
        <p>
          Regular diabetes eye checks and early detection can save your sight.
        </p>
        <div className="mb-1 flex">
          (All required fields are marked with a red dot
          <RequiredDot />
          .)
        </div>
      </div>
      <div className="hidden md:block ml-7 -mt-5">
        <img alt="Calender icon" src="/images/reminder.png" />
      </div>
    </div>
  </>
);

const DefaultSubmitBox = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  name,
}) => {
  const Consent = () => (
    <>
      I agree to the{" "}
      <a href="/terms_conditions" className="link" target="_blank">
        terms and conditions
      </a>
    </>
  );

  const ariaDescribedBy = `${name}-desc`;

  return (
    <>
      <div className="ml-1 my-3" data-testid="patient-consent">
        <div className="flex">
          <Checkbox
            id="patient_consent"
            name={name}
            label={<Consent />}
            value={values.patient_consent}
            defaultChecked={values.patient_consent === "true"}
            onChange={handleChange}
            onBlur={handleBlur}
            ariaDescribedBy={errors.patient_consent && ariaDescribedBy}
            ariaRequired="true"
            ariaInvalid={(errors.patient_consent && "true") || "false"}
          />
          <RequiredDot />
        </div>
        {errors.patient_consent && touched.patient_consent && (
          <Error id={ariaDescribedBy} errorText={errors.patient_consent} />
        )}
      </div>

      <div className="my-2">
        <Button
          name="enrolment_submit_button"
          label="Join Keepsight"
          type="submit"
        />
      </div>
    </>
  );
};

function PatientEnrolmentForm({
  values: initialValues,
  saveEnrolmentForm,
  choiceCode,
  SignUpExplainer = DefaultSignUpExplainer,
  SubmitBox = DefaultSubmitBox,
}) {
  const validAustralianMobile = (number) => {
    try {
      const parsedNumber = parsePhoneNumber(number, "AU");
      return parsedNumber.isValid() && parsedNumber.getType() == "MOBILE";
    } catch (error) {
      return false;
    }
  };

  const fieldsMargin = "my-4";

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors = {};

          if (!values.title) {
            errors.title = "Please select a valid title";
          }

          if (!values.first_name) {
            errors.first_name = "Please enter a first name, e.g. Sam";
          }

          if (!values.surname) {
            errors.surname = "Please enter a surname, e.g. Smith";
          }

          if (!values.dob) {
            errors.dob = "Please enter a valid date, e.g. 11/04/1980";
          } else if (!moment(values.dob, "YYYY-MM-DD", true).isValid()) {
            errors.dob = "Please enter a valid date, e.g. 11/04/1980";
          } else if (
            moment(values.dob, "YYYY-MM-DD").diff(Date.now(), "days") >= 0
          ) {
            errors.dob = "Please enter a date before today";
          }

          if (!values.postcode.match(postcodeRegex)) {
            errors.postcode =
              "Please enter a 4 digit postcode number, e.g. 3000";
          }

          if (values.has_ndss === "yes") {
            if (!values.ndss.match(ndssRegex)) {
              errors.ndss = "Please enter a 5-9 digit number, e.g. 12345";
            }
          }

          if (!values.last_eye_test) {
            errors.last_eye_test =
              "Please select how long ago your last eye test was";
          }

          if (values.follow_up_sms) {
            if (!values.follow_up_sms_number) {
              errors.follow_up_sms_number =
                "Phone number must be australian mobile, e.g. 0405 123 456";
            } else if (!validAustralianMobile(values.follow_up_sms_number)) {
              errors.follow_up_sms_number =
                "Phone number must be australian mobile, e.g. 0405 123 456";
            }
          }

          if (values.follow_up_email) {
            if (!values.follow_up_email_address) {
              errors.follow_up_email_address =
                "Please enter a valid email, e.g. user@example.com";
            } else if (
              !values.follow_up_email_address.match(emailAddressRegex)
            ) {
              errors.follow_up_email_address =
                "Please enter a valid email, e.g. user@example.com";
            } else if (
              invalidEmailAddresses.includes(
                values.follow_up_email_address.trim(),
              )
            ) {
              errors.follow_up_email_address =
                values.follow_up_email_address +
                " is not allowed. Please enter a different email address.";
            }
          }

          if (values.follow_up_post) {
            if (!values.follow_up_post_address) {
              errors.follow_up_post_address =
                "A follow up postal address is required, e.g. 123 Street, Brunswick, Melbourne";
            }
          }

          if (
            !(
              values.follow_up_email ||
              values.follow_up_post ||
              values.follow_up_sms
            )
          ) {
            errors.follow_up_email = "Please enter a contact email address";

            errors.communication_preferences =
              "Please select how you would like to be contacted";
          }

          if (values.patient_consent === false) {
            errors.patient_consent =
              "Please provide your consent by checking the box please";
          }

          return errors;
        }}
        onSubmit={(values) => {
          const formattedValues = {
            ...values,
            follow_up_email_address:
              values.follow_up_email && values.follow_up_email_address
                ? values.follow_up_email_address.replace(/\s/g, "")
                : "",
            follow_up_sms_number:
              values.follow_up_sms && values.follow_up_sms_number
                ? values.follow_up_sms_number.replace(/\s/g, "")
                : "",
          };
          saveEnrolmentForm(formattedValues);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form
            onSubmit={handleSubmit}
            autoComplete="off"
            data-testid="enrolment-form"
          >
            <FormikErrorFocus
              offset={-50}
              align={"top"}
              focusDelay={200}
              ease={"linear"}
              duration={250}
            />
            <div className="m-4 md:my-6 md:max-w-3xl">
              <SignUpExplainer />

              <div className="md:max-w-xl">
                <div className="my-6 p-4 border border-gray-400">
                  <FormHeader>
                    <div>Your details</div>
                  </FormHeader>

                  <div>
                    <div className={fieldsMargin}>
                      <Select
                        name="title"
                        label="Title"
                        autoComplete="honorific-prefix"
                        required
                      >
                        <option value="" disabled />
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Miss">Miss</option>
                        <option value="Ms">Ms</option>
                        <option value="Dr">Dr</option>
                        <option value="Prof">Prof</option>
                        <option value="Rev">Rev</option>
                      </Select>
                    </div>

                    <div className={fieldsMargin}>
                      <TextFieldWithLabel
                        ariaDescribedBy="first_name"
                        autoComplete="given-name"
                        label="First Name"
                        name="first_name"
                        required
                      />
                    </div>

                    <div className={fieldsMargin}>
                      <TextFieldWithLabel
                        autoComplete="family-name"
                        label="Surname"
                        name="surname"
                        required
                      />
                    </div>

                    <div>
                      <div>
                        <div id="dob_form_group">
                          <div className="mb-1 flex">
                            <Label name="dob">Date of Birth</Label>
                            <RequiredDot />
                          </div>
                          <div>
                            <DateField name="dob" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className={fieldsMargin}>
                        <TextFieldWithLabel
                          label="Postcode"
                          name="postcode"
                          required
                        />
                      </div>
                    </div>

                    <div className={fieldsMargin}>
                      <div className="mb-1">
                        <label>
                          Are you registered on the National Diabetes Services
                          Scheme?
                        </label>
                      </div>
                      <div
                        id="has_ndss_form_group"
                        data-testid="has_ndss-question"
                      >
                        <div>
                          <div>
                            <Radio
                              id="has_ndss_yes"
                              name="has_ndss"
                              label="Yes"
                              value="yes"
                              checked={values.has_ndss === "yes"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                          <div>
                            <Radio
                              id="has_ndss_no"
                              name="has_ndss"
                              label="No"
                              value="no"
                              checked={values.has_ndss === "no"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {values.has_ndss === "yes" && (
                      <div className={fieldsMargin}>
                        <TextFieldWithLabel
                          ariaDescribedBy="ndss_card_hint"
                          label="NDSS registration number"
                          name="ndss"
                          placeholder="e.g. 12345678"
                          required
                        />
                        <p id="ndss_card_hint" className="my-2">
                          Your NDSS number can be found on your NDSS card, above
                          your name.
                        </p>
                        <div className="flex items-center">
                          <img
                            src="/images/ndss-card.png"
                            alt="Image of NDSS card"
                            className="w-60"
                            style={{ marginLeft: "-7px" }}
                          />
                        </div>
                      </div>
                    )}

                    {values.has_ndss === "no" && (
                      <div className={fieldsMargin}>
                        <p>
                          Don't have an NDSS registration number?
                          <br />
                          <a
                            className="underline italic"
                            target="_blank"
                            href="https://www.ndss.com.au/about-the-ndss/registration/"
                          >
                            Find out more about how to obtain an NDSS
                            registration
                          </a>
                        </p>
                      </div>
                    )}

                    {values.has_ndss === "no" && (
                      <div className={fieldsMargin}>
                        <div className="mb-1">
                          <label>
                            Are you of Aboriginal and/or Torres Strait Islander
                            origin?
                          </label>
                        </div>
                        <div
                          id="indigenous_form_group"
                          data-testid="indigenous-question"
                        >
                          <div>
                            <div>
                              <Radio
                                id="indigenous_yes"
                                name="indigenous"
                                label="Yes"
                                value="yes"
                                checked={values.indigenous === "yes"}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div>
                              <Radio
                                id="indigenous_no"
                                name="indigenous"
                                label="No"
                                value="no"
                                checked={values.indigenous === "no"}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div>
                              <Radio
                                id="indigenous_prefer_not_to_say"
                                name="indigenous"
                                label="Unknown / Prefer not to say"
                                value="prefer_not_to_say"
                                checked={
                                  values.indigenous === "prefer_not_to_say"
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={`my-6 p-4 border border-gray-400  ${
                    choiceCode ? "hidden" : ""
                  }`}
                  data-testid="eye-check-question"
                >
                  <FormHeader>
                    <div>When was your last diabetes eye check?</div>
                  </FormHeader>

                  <div>
                    <div>
                      <div id="last_eye_test_form_group">
                        <p>
                          A <em>diabetes eye check</em> specifically looks for
                          any signs that your diabetes is affecting your eyes,
                          and will usually involve photos being taken of the
                          back your eyes.
                        </p>
                        <div className="mb-1 flex">
                          <Label name="last_eye_test">
                            Choose when your last diabetes eye check was
                          </Label>
                          <RequiredDot />
                        </div>

                        <fieldset
                          aria-label={
                            touched.last_eye_test && errors.last_eye_test
                              ? `When was your last eye check? Error - ${errors.last_eye_test}`
                              : "When was your last eye check?"
                          }
                          aria-required="true"
                        >
                          <div className="mb-2">
                            <Radio
                              id="last_eye_test_less_6"
                              name="last_eye_test"
                              label={eyeTestCodeToLabel("less_6_months")}
                              value="less_6_months"
                              checked={values.last_eye_test === "less_6_months"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ariaDescribedBy={
                                errors.last_eye_test && "last_eye_check_error"
                              }
                            />
                          </div>
                          <div className="mb-2">
                            <Radio
                              id="last_eye_test_6_12"
                              name="last_eye_test"
                              label={eyeTestCodeToLabel("6_12_months")}
                              value="6_12_months"
                              checked={values.last_eye_test === "6_12_months"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ariaDescribedBy={
                                errors.last_eye_test && "last_eye_check_error"
                              }
                            />
                          </div>
                          <div className="mb-2">
                            <Radio
                              id="last_eye_test_1_2"
                              name="last_eye_test"
                              label={eyeTestCodeToLabel("1_2_years")}
                              value="1_2_years"
                              checked={values.last_eye_test === "1_2_years"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ariaDescribedBy={
                                errors.last_eye_test && "last_eye_check_error"
                              }
                            />
                          </div>
                          <div className="mb-2">
                            <Radio
                              id="last_eye_test_over_2"
                              name="last_eye_test"
                              label={eyeTestCodeToLabel("over_2_years")}
                              value="over_2_years"
                              checked={values.last_eye_test === "over_2_years"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ariaDescribedBy={
                                errors.last_eye_test && "last_eye_check_error"
                              }
                            />
                          </div>
                          <div className="mb-2">
                            <Radio
                              id="last_eye_test_unknown_never"
                              name="last_eye_test"
                              label={eyeTestCodeToLabel("unknown_never")}
                              value="unknown_never"
                              checked={values.last_eye_test === "unknown_never"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ariaDescribedBy={
                                errors.last_eye_test && "last_eye_check_error"
                              }
                            />
                          </div>
                        </fieldset>
                      </div>
                      {touched.last_eye_test && errors.last_eye_test && (
                        <Error
                          id="last_eye_check_error"
                          errorText={errors.last_eye_test}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="my-6 p-4 border border-gray-400">
                  <fieldset
                    aria-label={
                      (touched.follow_up_sms ||
                        touched.follow_up_email ||
                        touched.follow_up_post) &&
                      errors.communication_preferences
                        ? `How would you like to be contacted? Error - ${errors.communication_preferences}`
                        : "How would you like to be contacted?"
                    }
                    aria-required="true"
                  >
                    <legend>
                      <FormHeader>
                        <div>How would you like to be reminded?</div>
                      </FormHeader>
                    </legend>
                    <div className="mb-1 flex">
                      <Label>Select all that apply</Label>
                      <RequiredDot />
                    </div>
                    <CheckboxWithField
                      name="follow_up_sms"
                      label="SMS"
                      value={values.follow_up_sms}
                      ariaDescribedBy={
                        errors.communication_preferences &&
                        "communication_errors"
                      }
                    >
                      <div className="mb-2">
                        <TextFieldWithLabel
                          label=""
                          name="follow_up_sms_number"
                          placeholder="Your mobile number"
                          ariaRequired={true}
                          ariaInvalid={
                            (errors.follow_up_sms_number && "true") || "false"
                          }
                        />
                      </div>
                    </CheckboxWithField>

                    <CheckboxWithField
                      name="follow_up_email"
                      label="Email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      ariaDescribedBy={
                        errors.communication_preferences &&
                        "communication_errors"
                      }
                    >
                      <div className="mb-2">
                        <TextFieldWithLabel
                          label=""
                          name="follow_up_email_address"
                          placeholder="Your email address"
                          ariaRequired={true}
                          ariaInvalid={
                            (errors.follow_up_email_address && "true") ||
                            "false"
                          }
                        />
                      </div>
                    </CheckboxWithField>

                    <CheckboxWithField
                      name="follow_up_post"
                      label="Post"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      ariaDescribedBy={
                        errors.communication_preferences &&
                        "communication_errors"
                      }
                    >
                      <div className="mb-2" data-testid="places-search-box">
                        <GoogleAddress
                          id="follow_up_post_address"
                          name="follow_up_post_address"
                          value={values.follow_up_post_address}
                          initialValue={values.follow_up_post_address}
                          onChange={(e) => {
                            handleChange(e);
                            setFieldValue("follow_up_post_address", e);
                          }}
                          onBlur={handleBlur}
                          ariaRequired={true}
                          ariaDescribedBy={
                            errors.follow_up_post_address &&
                            "communication_errors"
                          }
                          ariaInvalid={
                            (errors.follow_up_post_address && "true") || "false"
                          }
                        />
                        {touched.follow_up_post_address &&
                          errors.follow_up_post_address && (
                            <Error errorText={errors.follow_up_post_address} />
                          )}
                      </div>
                    </CheckboxWithField>

                    <div id="communication_errors">
                      {(touched.follow_up_sms ||
                        touched.follow_up_email ||
                        touched.follow_up_post) &&
                        errors.communication_preferences && (
                          <Error errorText={errors.communication_preferences} />
                        )}
                    </div>
                  </fieldset>
                </div>

                <SubmitBox
                  values={values}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  isSubmitting={isSubmitting}
                  name="patient_consent"
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default PatientEnrolmentForm;
